// =====================================================
// ADMINISTRACIÓN DE CREDENCIALES DE SERVICIOS EXTERNOS
// src/pages/Agro/Credenciales/index.js
//
// Pantalla propia y no un tab más de Administración, por tres razones:
//   1. El control de acceso va a ser distinto (BDSJEA-13): administrar
//      precios de granos no es lo mismo que rotar la API key de producción.
//      Con ruta propia se puede proteger por separado.
//   2. Administracion/index.js ya tiene más de 1800 líneas.
//   3. Esta pantalla maneja valores sensibles y conviene poder auditarla sin
//      leer el resto.
//
// NUNCA muestra el valor de una credencial. El backend no lo devuelve, y esta
// pantalla no lo guarda en estado más allá del submit.
// =====================================================

import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Spinner,
  Alert,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormGroup,
  Label,
} from "reactstrap"
import dayjs from "dayjs"

import Layout from "components/Layout"
import toastHandler from "toastHandler"

import {
  getCredenciales,
  rotarSecretoCredencial,
  cambiarEstadoCredencial,
  probarCredencial,
  actualizarCredencial,
  getAuditoriaCredencial,
} from "api/agro"

const ETIQUETA_TIPO = {
  API_KEY: "API Key",
  CLIENT_CREDENTIALS: "Client ID / Secret",
  USER_PASSWORD: "Usuario y contraseña",
  KEYSTORE: "Certificado",
  NINGUNA: "Sin credencial",
}

const AdministracionCredenciales = () => {
  const permissionBy = "agro.alta"

  // toastHandler es un store de zustand, no una libreria de toasts: se
  // consume como hook y expone setError / setSuccess / setWarnings.
  const { setError, setSuccess, setWarnings } = toastHandler()

  const [loading, setLoading] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [credenciales, setCredenciales] = useState([])
  const [proveedor, setProveedor] = useState(null)

  const [modalSecreto, setModalSecreto] = useState(false)
  const [modalMetadata, setModalMetadata] = useState(false)
  const [modalAuditoria, setModalAuditoria] = useState(false)

  const [seleccionada, setSeleccionada] = useState(null)
  const [valorSecreto, setValorSecreto] = useState("")
  const [mostrarValor, setMostrarValor] = useState(false)
  const [vencimiento, setVencimiento] = useState("")
  const [observacion, setObservacion] = useState("")
  const [auditoria, setAuditoria] = useState([])
  const [probando, setProbando] = useState(null)

  useEffect(() => {
    cargar()
  }, [])

  const cargar = async () => {
    setLoading(true)
    try {
      const data = await getCredenciales()
      setCredenciales(data?.credenciales || [])
      setProveedor(data?.proveedor || null)
    } catch (e) {
      setError("No se pudieron cargar las credenciales")
    } finally {
      setLoading(false)
    }
  }

  // ---------------------------------------------------------------
  // Carga / rotación del secreto
  // ---------------------------------------------------------------

  const abrirModalSecreto = credencial => {
    setSeleccionada(credencial)
    setValorSecreto("")
    setMostrarValor(false)
    setModalSecreto(true)
  }

  const cerrarModalSecreto = () => {
    // Se limpia el valor del estado al cerrar, siempre. Si el usuario cancela
    // a mitad de camino, la credencial no queda dando vueltas en memoria del
    // componente ni visible en las React DevTools.
    setValorSecreto("")
    setMostrarValor(false)
    setModalSecreto(false)
    setSeleccionada(null)
  }

  const guardarSecreto = async () => {
    if (!valorSecreto.trim()) {
      setWarnings("Ingresá el valor de la credencial")
      return
    }

    setGuardando(true)
    try {
      const r = await rotarSecretoCredencial(seleccionada.id, valorSecreto)
      setSuccess(
        `Secreto cargado (versión ${r.version}). Ejecutá el test antes de activar.`,
      )
      cerrarModalSecreto()
      cargar()
    } catch (e) {
      // setError sabe desestructurar un error de axios, pero el backend
      // devuelve el detalle en data.error y no en data.message, asi que se
      // extrae a mano y se pasa el string.
      setError(e?.response?.data?.error || "No se pudo guardar la credencial")
    } finally {
      setGuardando(false)
    }
  }

  // ---------------------------------------------------------------

  const alternarActivo = async credencial => {
    try {
      await cambiarEstadoCredencial(credencial.id, !credencial.activo)
      setSuccess(
        credencial.activo ? "Credencial desactivada" : "Credencial activada",
      )
      cargar()
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo cambiar el estado")
    }
  }

  const probar = async credencial => {
    setProbando(credencial.id)
    try {
      const r = await probarCredencial(credencial.id)
      if (r.exitoso) {
        setSuccess(r.mensaje || "Conexión exitosa")
      } else {
        // Un test fallido no es un error de la aplicación: es información.
        // Por eso va como warning y no como error.
        setWarnings(r.mensaje || "El test falló")
      }
      cargar()
    } catch (e) {
      setError("No se pudo ejecutar el test")
    } finally {
      setProbando(null)
    }
  }

  const abrirModalMetadata = credencial => {
    setSeleccionada(credencial)
    setVencimiento(credencial.fechaVencimiento || "")
    setObservacion(credencial.observacion || "")
    setModalMetadata(true)
  }

  const guardarMetadata = async () => {
    setGuardando(true)
    try {
      await actualizarCredencial(seleccionada.id, {
        fechaVencimiento: vencimiento || null,
        observacion,
      })
      setSuccess("Credencial actualizada")
      setModalMetadata(false)
      cargar()
    } catch (e) {
      setError("No se pudo actualizar")
    } finally {
      setGuardando(false)
    }
  }

  const verAuditoria = async credencial => {
    setSeleccionada(credencial)
    setModalAuditoria(true)
    try {
      setAuditoria(await getAuditoriaCredencial(credencial.codigoServicio))
    } catch (e) {
      setAuditoria([])
      setError("No se pudo cargar la auditoría")
    }
  }

  // ---------------------------------------------------------------

  const badgeEstado = c => {
    if (c.tipoCredencial === "NINGUNA") {
      return (
        <Badge color="light" className="text-muted">
          No requiere
        </Badge>
      )
    }
    if (!c.tieneSecretoCargado) {
      return <Badge color="secondary">Sin cargar</Badge>
    }
    if (!c.activo) {
      return <Badge color="warning">Inactiva</Badge>
    }
    if (c.diasParaVencer != null && c.diasParaVencer < 0) {
      return <Badge color="danger">Vencida</Badge>
    }
    if (c.diasParaVencer != null && c.diasParaVencer <= 60) {
      return <Badge color="warning">Vence en {c.diasParaVencer} d</Badge>
    }
    return <Badge color="success">Activa</Badge>
  }

  const badgeTest = c => {
    if (c.ultimoTestExitoso == null) {
      return <span className="text-muted small">Sin probar</span>
    }
    return (
      <span
        className={`small text-${c.ultimoTestExitoso ? "success" : "danger"}`}
        title={c.ultimoTestMensaje || ""}
      >
        <i
          className={`mdi ${
            c.ultimoTestExitoso ? "mdi-check-circle" : "mdi-alert-circle"
          } me-1`}
        />
        {dayjs(c.ultimoTestFecha).format("DD/MM HH:mm")}
      </span>
    )
  }

  const breadcrumbItems = [
    { title: "Agropecuario", link: "/agro" },
    { title: "Credenciales", link: "#" },
  ]

  const sinEscritura = proveedor && !proveedor.soportaEscritura

  const contenido = (
    <>
      {/* Estado del proveedor de secretos. Es lo primero que hay que ver:
          si el proveedor es PROPERTIES no se puede cargar nada, y sin este
          cartel el botón simplemente fallaría sin explicación. */}
      {proveedor && (
        <Alert color={sinEscritura ? "warning" : "info"} className="mb-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <i className="mdi mdi-shield-key-outline me-2" />
              Almacén de secretos: <strong>{proveedor.tipo}</strong>
              {" · "}
              {proveedor.disponible ? "disponible" : "no disponible"}
              {sinEscritura && (
                <div className="small mt-1">
                  Este almacén es de solo lectura. Para cargar credenciales
                  desde la aplicación hay que configurar Key Vault (BDSJEA-36).
                </div>
              )}
            </div>
            <Button color="light" size="sm" onClick={cargar}>
              <i className="mdi mdi-refresh me-1" />
              Actualizar
            </Button>
          </div>
        </Alert>
      )}

      <Card>
        <CardBody>
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <Table responsive hover className="mb-0 align-middle">
              <thead>
                <tr className="text-muted" style={{ fontSize: "0.75rem" }}>
                  <th>Servicio</th>
                  <th>Tipo</th>
                  <th>Estado</th>
                  <th>Vencimiento</th>
                  <th>Último test</th>
                  <th className="text-end">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {credenciales.map(c => (
                  <tr key={c.id}>
                    <td>
                      <div className="fw-semibold">{c.nombreServicio}</div>
                      <div
                        className="text-muted"
                        style={{ fontSize: "0.7rem" }}
                      >
                        {c.codigoServicio}
                        {c.referenciaSecreto && ` · ${c.referenciaSecreto}`}
                      </div>
                    </td>
                    <td className="small">
                      {ETIQUETA_TIPO[c.tipoCredencial] || c.tipoCredencial}
                    </td>
                    <td>{badgeEstado(c)}</td>
                    <td className="small">
                      {c.fechaVencimiento
                        ? dayjs(c.fechaVencimiento).format("DD/MM/YYYY")
                        : "—"}
                    </td>
                    <td>{badgeTest(c)}</td>
                    <td className="text-end text-nowrap">
                      {c.tipoCredencial !== "NINGUNA" && (
                        <>
                          <Button
                            color="link"
                            size="sm"
                            className="p-1"
                            disabled={sinEscritura}
                            title={
                              sinEscritura
                                ? "El almacén de secretos actual es de solo lectura"
                                : "Cargar o rotar el valor"
                            }
                            onClick={() => abrirModalSecreto(c)}
                          >
                            <i className="mdi mdi-key-change" />
                          </Button>

                          <Button
                            color="link"
                            size="sm"
                            className="p-1"
                            disabled={
                              !c.tieneSecretoCargado || probando === c.id
                            }
                            title="Probar conexión"
                            onClick={() => probar(c)}
                          >
                            {probando === c.id ? (
                              <Spinner size="sm" />
                            ) : (
                              <i className="mdi mdi-lan-connect" />
                            )}
                          </Button>

                          <Button
                            color="link"
                            size="sm"
                            className="p-1"
                            disabled={!c.tieneSecretoCargado}
                            title={c.activo ? "Desactivar" : "Activar"}
                            onClick={() => alternarActivo(c)}
                          >
                            <i
                              className={`mdi ${
                                c.activo
                                  ? "mdi-toggle-switch"
                                  : "mdi-toggle-switch-off"
                              }`}
                            />
                          </Button>
                        </>
                      )}

                      <Button
                        color="link"
                        size="sm"
                        className="p-1"
                        title="Editar vencimiento y observación"
                        onClick={() => abrirModalMetadata(c)}
                      >
                        <i className="mdi mdi-pencil-outline" />
                      </Button>

                      <Button
                        color="link"
                        size="sm"
                        className="p-1"
                        title="Ver auditoría"
                        onClick={() => verAuditoria(c)}
                      >
                        <i className="mdi mdi-history" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* ---------- Modal: cargar / rotar secreto ---------- */}
      <Modal isOpen={modalSecreto} toggle={cerrarModalSecreto} centered>
        <ModalHeader toggle={cerrarModalSecreto}>
          {seleccionada?.tieneSecretoCargado ? "Rotar" : "Cargar"} credencial
        </ModalHeader>
        <ModalBody>
          <p className="text-muted small">
            {seleccionada?.nombreServicio} ·{" "}
            {ETIQUETA_TIPO[seleccionada?.tipoCredencial]}
          </p>

          <FormGroup>
            <Label className="small">Valor</Label>
            <div className="d-flex">
              <Input
                type={mostrarValor ? "text" : "password"}
                value={valorSecreto}
                onChange={e => setValorSecreto(e.target.value)}
                autoComplete="new-password"
                spellCheck={false}
                placeholder="Pegar el valor de la credencial"
              />
              <Button
                color="light"
                className="ms-2"
                type="button"
                onClick={() => setMostrarValor(!mostrarValor)}
                title={mostrarValor ? "Ocultar" : "Mostrar"}
              >
                <i
                  className={`mdi ${mostrarValor ? "mdi-eye-off" : "mdi-eye"}`}
                />
              </Button>
            </div>
          </FormGroup>

          <Alert color="info" className="small mb-0">
            <i className="mdi mdi-information-outline me-1" />
            El valor se escribe directamente en el almacén de secretos y no se
            guarda en la base de datos ni vuelve a mostrarse. La credencial{" "}
            <strong>no queda activa</strong>: hay que probar la conexión y
            activarla.
          </Alert>

          {seleccionada?.tieneSecretoCargado && (
            <Alert color="warning" className="small mt-2 mb-0">
              Ya hay un valor cargado. Al guardar se crea una versión nueva; la
              anterior queda disponible para rollback en el almacén.
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            onClick={cerrarModalSecreto}
            disabled={guardando}
          >
            Cancelar
          </Button>
          <Button color="primary" onClick={guardarSecreto} disabled={guardando}>
            {guardando ? <Spinner size="sm" /> : "Guardar"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ---------- Modal: metadata ---------- */}
      <Modal
        isOpen={modalMetadata}
        toggle={() => setModalMetadata(false)}
        centered
      >
        <ModalHeader toggle={() => setModalMetadata(false)}>
          Editar {seleccionada?.nombreServicio}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label className="small">Fecha de vencimiento</Label>
            <Input
              type="date"
              value={vencimiento}
              onChange={e => setVencimiento(e.target.value)}
            />
            <div className="text-muted mt-1" style={{ fontSize: "0.75rem" }}>
              Se avisa con 60 días de anticipación.
            </div>
          </FormGroup>
          <FormGroup>
            <Label className="small">Observación</Label>
            <Input
              type="textarea"
              rows={3}
              value={observacion}
              onChange={e => setObservacion(e.target.value)}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setModalMetadata(false)}>
            Cancelar
          </Button>
          <Button
            color="primary"
            onClick={guardarMetadata}
            disabled={guardando}
          >
            {guardando ? <Spinner size="sm" /> : "Guardar"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ---------- Modal: auditoría ---------- */}
      <Modal
        isOpen={modalAuditoria}
        toggle={() => setModalAuditoria(false)}
        size="lg"
        centered
      >
        <ModalHeader toggle={() => setModalAuditoria(false)}>
          Auditoría · {seleccionada?.nombreServicio}
        </ModalHeader>
        <ModalBody>
          {auditoria.length === 0 ? (
            <p className="text-muted mb-0">Sin movimientos registrados.</p>
          ) : (
            <Table size="sm" className="mb-0">
              <thead>
                <tr className="text-muted" style={{ fontSize: "0.7rem" }}>
                  <th>Fecha</th>
                  <th>Acción</th>
                  <th>Detalle</th>
                  <th>Usuario</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody style={{ fontSize: "0.8rem" }}>
                {auditoria.map(a => (
                  <tr key={a.id}>
                    <td className="text-nowrap">
                      {dayjs(a.fecha).format("DD/MM/YY HH:mm")}
                    </td>
                    <td>
                      <Badge
                        color={a.exitoso ? "light" : "danger"}
                        className="text-dark"
                      >
                        {a.accion}
                      </Badge>
                    </td>
                    <td>{a.detalle || "—"}</td>
                    <td>{a.usuario}</td>
                    <td className="text-muted">{a.direccionIp || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </ModalBody>
      </Modal>
    </>
  )

  return (
    <Layout
      title="Credenciales de servicios"
      breadcrumbItems={breadcrumbItems}
      permissionBy={permissionBy}
      components={[{ component: contenido }]}
    />
  )
}

export default AdministracionCredenciales
